import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { IMAGES } from "@/assets";
import { Reveal } from "@/components/Reveal";

const chapters = [
    {
        num: "01",
        title: "Pool Cleaning",
        copy: "Full valet cleans — surface skimming, vacuuming, brushing and basket clears. We leave every pool sparkling and every deck tidy.",
        image: IMAGES.poolFull,
        alt: "Freshly cleaned backyard pool with brick paving",
        testid: "service-image-cleaning",
    },
    {
        num: "02",
        title: "Regular Maintenance",
        copy: "Scheduled weekly or fortnightly visits. Water testing, chemical balancing and filter checks so small issues never become big ones.",
        image: IMAGES.vacuum,
        alt: "Pool vacuum hose gliding across clear blue water",
        testid: "service-image-maintenance",
    },
    {
        num: "03",
        title: "Equipment Checks & Repairs",
        copy: "Pumps, filters, chlorinators and water features — inspected, serviced and repaired before they let you down mid-summer.",
        image: IMAGES.waterfall,
        alt: "Stone water feature wall pouring into a pool",
        testid: "service-image-equipment",
    },
    {
        num: "04",
        title: "Green Pool Recovery",
        copy: "From swamp to swim-ready. Shock treatment, algaecide and deep cleans that bring even the most neglected pools back to life.",
        image: IMAGES.water,
        alt: "Sparkling turquoise pool water after recovery",
        testid: "service-image-recovery",
    },
];

const ChapterImage = ({ chapter }) => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
    const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

    return (
        <div ref={ref} className="relative overflow-hidden aspect-[3/4] max-h-[560px] w-full">
            <motion.img
                src={chapter.image}
                alt={chapter.alt}
                data-testid={chapter.testid}
                style={{ y }}
                className="absolute inset-0 w-full h-[116%] object-cover transition-transform duration-700 ease-out hover:scale-105"
            />
        </div>
    );
};

const Services = () => (
    <section id="services" data-testid="services-section" className="relative py-28 md:py-40">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
            <Reveal>
                <p className="text-xs uppercase tracking-[0.3em] text-[#00E5FF] mb-4">What we do</p>
                <h2 className="font-display tracking-tighter text-4xl sm:text-5xl lg:text-6xl text-[#F4F4F0] max-w-3xl leading-[1.05]">
                    Four chapters of <span className="italic text-[#00E5FF]">pool care</span>
                </h2>
            </Reveal>

            <div className="mt-24 md:mt-32 flex flex-col gap-28 md:gap-40">
                {chapters.map((c, i) => (
                    <div
                        key={c.num}
                        data-testid={`service-chapter-${c.num}`}
                        className={`grid md:grid-cols-12 gap-10 md:gap-16 items-center`}
                    >
                        <div className={`md:col-span-7 ${i % 2 === 1 ? "md:order-2" : ""}`}>
                            <Reveal>
                                <span className="font-display outline-text text-[18vw] md:text-[9rem] leading-none block select-none">
                                    {c.num}
                                </span>
                                <h3 className="font-display tracking-tight text-3xl md:text-5xl text-[#F4F4F0] mt-2 md:-mt-6">
                                    {c.title}
                                </h3>
                                <p className="mt-6 text-sm md:text-base text-[#8A949E] leading-relaxed max-w-lg">
                                    {c.copy}
                                </p>
                            </Reveal>
                        </div>
                        <Reveal delay={0.15} className={`md:col-span-5 ${i % 2 === 1 ? "md:order-1" : ""}`}>
                            <ChapterImage chapter={c} />
                        </Reveal>
                    </div>
                ))}
            </div>
        </div>
    </section>
);

export default Services;
